import { Box, Divider, Skeleton, Spacer } from '@eatclub-apps/ec-component-library';
import { useTheme } from '@material-ui/core';
import { Editor, EditorState, convertFromRaw } from 'draft-js';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Button } from '../../../components/Button';
import { Typography } from '../../../components/Typography';
import { updateBookingStepAction } from '../../../data/actions/bookingAction';
import { fetchBookingTermsAction } from '../../../data/actions/bookingTermsAction';
import { updateObeeBookingAction } from '../../../data/actions/obeeBookingAction';
import { bookingPropTypes, bookingSteps } from '../../../data/models/Booking';
import { bookingTermsPropTypes } from '../../../data/models/BookingTerms';
import { addonGroupsPropTypes } from '../../../data/models/Experience';
import { restaurantPropTypes } from '../../../data/models/Restaurant';
import { obeeBookingPropTypes } from '../../../data/models/obeeBooking';
import { PageLayout } from '../../../layouts/PageLayout';
import { WidgetLayout } from '../../../layouts/WidgetLayout';
import { trackEvent } from '../../../utils/analytics';
import { getAddonGroupsForBooking, getExperienceTerms } from '../../../utils/bookingHelpers';
import { isObee } from '../../../utils/constants';
import { deepEqual, isEmpty, isVoid } from '../../../utils/helpers';
import { useIsMobile } from '../../../utils/hooks';

const Terms = ({
  booking, // The local booking being created/edited
  bookingTerms,
  obeeBooking, // The booking fetched from the API when editing
  restaurant,
  updateBookingStep,
  fetchBookingTerms,
  addonGroups,
}) => {
  const theme = useTheme();
  const isMobile = useIsMobile();

  // TODO does this apply to waitlist bookings?
  const waitlist = booking?.waitlist;

  const customTerms = getExperienceTerms(booking);

  const [loading, setLoading] = useState(
    isObee && isEmpty(customTerms) && isVoid(bookingTerms.data.terms_conditions),
  );

  /**
   * Fetch booking terms if haven't already (e.g. navigated directly to this page)
   */
  useEffect(() => {
    // note: EatClub has the terms included in the restaurant object
    if (!isObee || !isVoid(bookingTerms?.data?.terms_conditions)) {
      setLoading(false);
      return;
    }

    // If we have custom terms, no need to fetch
    if (!isEmpty(customTerms)) {
      return;
    }

    const { date, availabilitySuggestion, guests } = booking;

    const areaId = availabilitySuggestion?.table?.areaId || booking?.areaId;

    const newVariables = {
      restId: restaurant.data.objectId,
      areaId,
      date,
      time: availabilitySuggestion?.time,
      size: guests,
    };

    if (deepEqual(newVariables, bookingTerms.lastUsedVariables)) {
      // the terms have been fetched
      return;
    }

    fetchBookingTerms(areaId, date, availabilitySuggestion?.time, guests);
  }, [
    booking?.availabilitySuggestion?.time,
    booking?.availabilitySuggestion?.table?.areaId,
    booking?.areaId,
    booking?.guests,
    booking?.date,
  ]);

  const submit = () => {
    trackEvent('Navigation', 'Updating Step', 'Next Button');
    const addonGroupsForBooking = getAddonGroupsForBooking(booking, addonGroups);

    if (!isEmpty(addonGroupsForBooking.beforeDetails)) {
      updateBookingStep(bookingSteps.addonsBeforeDetails, 0);
    } else {
      updateBookingStep(bookingSteps.details, 0);
    }
  };

  const getTerms = () => {
    // If the experience has terms, use those instead
    const termsToUse = customTerms?.termsConditions ?? bookingTerms.data.terms_conditions;

    if (isEmpty(termsToUse)) {
      return null;
    }

    if (isObee) {
      return (
        <Typography style={{ whiteSpace: 'pre-line' }}>
          <div dangerouslySetInnerHTML={{ __html: termsToUse }} />
        </Typography>
      );
    }

    try {
      const contentState = convertFromRaw(JSON.parse(restaurant.data.terms));
      const editorState = EditorState.createWithContent(contentState);

      return <Editor editorState={editorState} readOnly onChange={() => {}} />;
    } catch {
      return null;
    }
  };

  if (!loading && isEmpty(getTerms())) {
    submit();
  }

  return (
    <PageLayout>
      <WidgetLayout showDetails showBackButton>
        <Spacer direction='vertical' gap={32}>
          <>
            <Spacer direction='vertical' gap={32}>
              <Typography variant='h2'>
                {customTerms?.dailyViewDisplay ?? 'Before you continue...'}
              </Typography>

              <Divider color={theme.colors.dividerColor} />

              {loading ? (
                <Skeleton height={240} />
              ) : (
                <Box
                  style={{
                    fontFamily: theme.baseTypography.availabilityFontFamily,
                    lineHeight: '1.4em',
                  }}
                >
                  {getTerms()}
                </Box>
              )}
              <Divider color={theme.colors.dividerColor} />
            </Spacer>
          </>

          <Box style={{ display: 'flex' }}>
            <Button
              size='large'
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                submit();
              }}
              disabled={loading}
              style={{ marginLeft: 'auto', minWidth: '144px' }}
              fullWidth={isMobile}
              color='primary'
            >
              Agree
            </Button>
          </Box>
        </Spacer>
      </WidgetLayout>
    </PageLayout>
  );
};

Terms.propTypes = {
  booking: bookingPropTypes.isRequired,
  bookingTerms: bookingTermsPropTypes.isRequired,
  obeeBooking: obeeBookingPropTypes.isRequired,
  restaurant: restaurantPropTypes.isRequired,
  updateBookingStep: PropTypes.func.isRequired,
  fetchBookingTerms: PropTypes.func.isRequired,
  addonGroups: addonGroupsPropTypes.isRequired,
};

const mapStateToProps = (state) => ({
  booking: state.booking,
  bookingTerms: state.bookingTerms,
  obeeBooking: state.obeeBooking,
  restaurant: state.restaurant,
  addonGroups: state.addonGroups,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      updateBookingStep: updateBookingStepAction,
      updateObeeBooking: updateObeeBookingAction,
      fetchBookingTerms: fetchBookingTermsAction,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(Terms);
